/**
 * Cursor state machine for infinite scroll.
 *
 * Ported from raftor_webhq/public/js/infinite_scroll.js — the pagination half
 * only. That file's card rendering (createProductHTML/getPriceHTML) is gone;
 * appended pages go through the same renderer as the first page.
 *
 * WHAT WAS DELETED, AND WHY IT COULD BE
 * ------------------------------------
 * captureStateFromDom(), the `selfMutation` flag with its 500ms window,
 * `pendingContextReset`, the 350ms-debounced MutationObserver on
 * #product-listing, and normalizeInitialStartFromDomFallback() all existed for
 * one reason: that code could not distinguish its own DOM writes from core's
 * re-renders, so it reconstructed pagination state by counting
 * document.querySelectorAll('.item-card').
 *
 * Owning the render removes the question. This class holds `start` and
 * `loadedIds` as plain state because it is the thing that appended them.
 * Context changes arrive through reset() as an explicit call, not by sniffing.
 *
 * WHAT WAS KEPT, because it is hard-won
 * ------------------------------------
 *  - termination precedence: server has_more → reliable total count →
 *    short-page heuristic (only for servers predating the signal)
 *  - identity is the route (INVARIANT 1), so dedupe survives swatch scripts
 *    rewriting a card's item code to a variant
 *  - bounded replay guard for the no-signal case
 *  - min interval between loads, and a preload offset so the next page starts
 *    before the shopper hits the very bottom
 */

const DEFAULTS = {
	minLoadIntervalMs: 350,
	preloadOffsetPx: 900,
	maxReplayGuardSkips: 3,
};

export class Paginator {
	/**
	 * @param {object} opts
	 * @param {{fetch:Function}} opts.source
	 * @param {Function} opts.onPage      (models, pagination) => void
	 * @param {Function} [opts.onStateChange] ({isLoading, hasMore, loadedCount}) => void
	 */
	constructor(opts) {
		const o = Object.assign({}, DEFAULTS, opts);
		this.source = o.source;
		this.onPage = o.onPage || (() => {});
		this.onStateChange = o.onStateChange || (() => {});
		this.minLoadIntervalMs = o.minLoadIntervalMs;
		this.preloadOffsetPx = o.preloadOffsetPx;
		this.maxReplayGuardSkips = o.maxReplayGuardSkips;

		this.filters = o.filters || {};
		this._reset();

		this._scrollHandler = null;
		this._scrollTimer = null;
	}

	_reset() {
		this.start = 0;
		this.hasMore = true;
		this.isLoading = false;
		this.loadedIds = new Set();
		this.pageLength = null;
		this.lastLoadAt = 0;
		this.replaySkips = 0;
	}

	get loadedCount() {
		return this.loadedIds.size;
	}

	_emit() {
		this.onStateChange({
			isLoading: this.isLoading,
			hasMore: this.hasMore,
			loadedCount: this.loadedCount,
		});
	}

	/**
	 * Context change (filter, sort, search). EXPLICIT — this is the narrow
	 * contract that replaced DOM sniffing. Callers that change the query call
	 * this; nothing infers it.
	 */
	reset(filters) {
		this.filters = filters || {};
		this._reset();
		this._emit();
	}

	/** Seed state from cards that were rendered by someone else (the Phase 4 shim). */
	seed(models, pagination) {
		for (const m of models || []) {
			if (m && m.id) this.loadedIds.add(m.id);
		}
		this.start = this.loadedIds.size;
		if (pagination) this._applyPagination(pagination, (models || []).length, 0);
		this._emit();
	}

	_applyPagination(pagination, returnedCount, uniqueCount) {
		if (!this.pageLength && pagination.pageLength) {
			this.pageLength = pagination.pageLength;
		}

		this.start = pagination.nextStart != null ? pagination.nextStart : this.start + returnedCount;

		// Termination precedence — server signal first, and only fall back when
		// it is genuinely absent (an older server), never when it says "false".
		if (pagination.hasMore !== null && pagination.hasMore !== undefined) {
			this.hasMore = pagination.hasMore;
		} else if (pagination.totalCount != null && pagination.countReliable) {
			this.hasMore = this.loadedIds.size < pagination.totalCount;
		} else {
			if (!this.pageLength) this.pageLength = returnedCount;
			this.hasMore = returnedCount >= this.pageLength;
		}

		// A whole page of already-seen products is a skip, not an end — unless
		// there is no authoritative signal, in which case give up after N.
		if (returnedCount > 0 && uniqueCount === 0) {
			this.replaySkips += 1;
			const noSignal =
				(pagination.hasMore === null || pagination.hasMore === undefined) &&
				!(pagination.totalCount != null && pagination.countReliable);
			if (noSignal && this.replaySkips > this.maxReplayGuardSkips) {
				this.hasMore = false;
			}
		} else if (uniqueCount > 0) {
			this.replaySkips = 0;
		}
	}

	/**
	 * True when a call was refused only because of the min-interval throttle —
	 * i.e. retrying later would succeed. Distinguishes "too soon" from
	 * "genuinely finished", which `loadMore()` returning null cannot.
	 */
	throttledNow() {
		if (this.isLoading || !this.hasMore) return false;
		return Boolean(this.lastLoadAt) && Date.now() - this.lastLoadAt < this.minLoadIntervalMs;
	}

	async loadMore() {
		if (this.isLoading || !this.hasMore) return null;

		const now = Date.now();
		if (this.lastLoadAt && now - this.lastLoadAt < this.minLoadIntervalMs) return null;

		this.isLoading = true;
		this.lastLoadAt = now;
		this._emit();

		try {
			const result = await this.source.fetch({ start: this.start, filters: this.filters });
			const models = (result && result.models) || [];
			const pagination = (result && result.pagination) || {};

			// Dedupe by route id. A safety net, never a terminator — an entirely
			// duplicate page still advances the cursor via _applyPagination.
			const unique = [];
			for (const model of models) {
				if (!model.id || this.loadedIds.has(model.id)) continue;
				this.loadedIds.add(model.id);
				unique.push(model);
			}

			this._applyPagination(pagination, models.length, unique.length);

			if (unique.length) this.onPage(unique, result);
			return { models: unique, pagination, settings: result && result.settings };
		} catch (err) {
			// A failed page must not permanently end the scroll; the shopper can
			// scroll again and retry.
			console.error('[rfhq paginator] load failed', err);
			return null;
		} finally {
			this.isLoading = false;
			this._emit();
		}
	}

	/** True when the viewport is within preloadOffsetPx of the document bottom. */
	shouldLoad(win, doc) {
		const w = win || window;
		const d = doc || document;
		const scrollPosition = w.innerHeight + w.scrollY;
		const documentHeight = Math.max(
			d.body.scrollHeight,
			d.documentElement.scrollHeight,
			d.body.offsetHeight,
			d.documentElement.offsetHeight
		);
		return scrollPosition >= documentHeight - this.preloadOffsetPx;
	}

	attachScroll() {
		if (this._scrollHandler || typeof window === 'undefined') return;

		this._scrollHandler = () => {
			clearTimeout(this._scrollTimer);
			this._scrollTimer = setTimeout(() => {
				if (this.shouldLoad()) this._loadAndContinue();
			}, 100);
		};
		window.addEventListener('scroll', this._scrollHandler, { passive: true });

		// A first page shorter than the viewport never fires a scroll event, so
		// it would sit there with more to load and no way to ask for it.
		this._loadAndContinue();
	}

	/**
	 * Keep fetching pages until at least `minCount` items are loaded, or the
	 * server says no more exist.
	 *
	 * Exists because a block's `max_items` and the site's `products_per_page`
	 * (Webshop Settings) are unrelated numbers — a block asking for 8 items on
	 * a site configured to serve 6 per page must not silently show 6. Without
	 * this, `load()`'s single `loadMore()` call took whatever the first page
	 * happened to contain and stopped, regardless of what was configured.
	 *
	 * Bounded at 20 iterations as a safety net against a pathological server
	 * response; the ordinary case (a genuinely exhausted listing) already
	 * terminates via `hasMore` turning false through the replay guard in
	 * `_applyPagination`.
	 */
	async loadUntil(minCount) {
		let guard = 0;
		while (this.loadedCount < minCount && this.hasMore && guard < 20) {
			guard += 1;
			if (this.throttledNow()) {
				await new Promise((resolve) => setTimeout(resolve, this.minLoadIntervalMs));
				continue;
			}
			const result = await this.loadMore();
			if (!result) break; // isLoading collision or a genuine stop; do not spin
		}
	}

	async _loadAndContinue() {
		// A throttled call is "try again shortly", not "we are done". Treating
		// the two the same is what stopped a first page shorter than the
		// viewport from ever loading page 2: load() sets lastLoadAt, then
		// attachScroll() calls straight back in and is refused, and without
		// this branch nothing ever rescheduled.
		if (this.throttledNow()) {
			setTimeout(() => {
				if (this.shouldLoad()) this._loadAndContinue();
			}, this.minLoadIntervalMs);
			return;
		}

		const result = await this.loadMore();
		// Appending changes scrollHeight; if we are still near the bottom, keep
		// going rather than waiting for a scroll event that may never come.
		if (result && this.hasMore) {
			setTimeout(() => {
				if (this.shouldLoad()) this._loadAndContinue();
			}, this.minLoadIntervalMs);
		}
	}

	detachScroll() {
		if (!this._scrollHandler || typeof window === 'undefined') return;
		window.removeEventListener('scroll', this._scrollHandler);
		clearTimeout(this._scrollTimer);
		this._scrollHandler = null;
	}
}
